import { ReactElement, SyntheticEvent, useEffect, useState } from 'react'
import {
  PageWrapper,
  SectionTitle,
  EditableDropdown,
  FlexWrapper,
  DropdownWrapper,
  Input,
  Button,
  Loader
} from '../../../components'
import strings from '../../../locale/en'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { PublishMaterialField } from './typings'
import validatePublishMaterialForm from './helpers'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../redux/store'
import {
  getBatchDropdown,
  getBranchDropdown,
  getCoursesDropdown,
  getInstituteDropdown,
  getMaterialsDropdown
} from '../../../helpers'
import { SelectedMaterial } from '../../../redux/materialAccess/types'
import { getInstitutes } from '../../../redux/coachingCenter/actions'
import {
  getAllCoursesByInstitute,
  getChildCourses,
  resetChapters,
  resetCourses,
  resetSubjects,
  resetTopics
} from '../../../redux/course/action'
import {
  getBranchesForCourse,
  resetBranches
} from '../../../redux/branch/action'
import { getBatchesForCourse, resetBatches } from '../../../redux/batch/action'
import {
  getMaterialsWithPublishStatus,
  saveMaterialAccess,
  resetMaterial,
  selectedMaterial
} from '../../../redux/materialAccess/action'
import { hasFormError } from '../../../helpers/formValidation'
import format from 'date-fns/format'
import {
  DATE_FORMAT_YYYYMMDD,
  TIME_FORMAT_HHMM
} from '../../../const/dateFormat'
import { DropdownListProps } from '../../../components/EditableDropdown/typings'
import { ROLE } from '../../../helpers/determineMenu'
import { useHistory } from 'react-router-dom'
import ROUTES from '../../../const/routes'

const PublishMaterial = (): ReactElement => {
  const {
    institutes = [],
    courses,
    branches,
    batches,
    subjects,
    chapters,
    topics,
    materials,
    selectedPublishMaterial,
    role,
    userDetail,
    batchesData,
    isLoading
  } = useSelector(
    (state: RootState) => ({
      institutes: state.coachingCenter.institutes,
      courses: state.course.courses,
      branches: state.branch.branches,
      branchesData: state.coachingCenter.branches,
      batches: state.batch.batches,
      batchesData: state.coachingCenter.batches,
      subjects: state.course.subjects,
      chapters: state.course.chapters,
      topics: state.course.topics,
      materials: state.material.materials,
      selectedPublishMaterial: state.material
        .selectedMaterial as SelectedMaterial,
      role: state.user.userInfo?.role,
      userDetail: state.user?.userInfo?.userDetail,
      isLoading: state.material.isLoading
    }),
    shallowEqual
  )

  const {
    menu: {
      admin: {
        instituteName,
        selectCourse,
        selectBranch,
        selectBatch,
        selectSubject,
        selectChapter,
        selectTopic,
        selectMaterial,
        publish,
        chapterPublish,
        publishDate,
        publishTiming
      }
    }
  } = strings
  const [startDate, setStartDate] = useState<any>(new Date())
  const [time, setTime] = useState<any>('')
  const dispatch = useDispatch()
  const history = useHistory()
  const isEdit = history.location.pathname !== ROUTES.PUBLISH_MATERIAL_ADD
  const [values, setValues] = useState(selectedPublishMaterial)
  const [errors, setErrors] = useState({} as Record<string, string>)
  const shouldHideInstitutes =
    role === ROLE.BRANCH_ADMIN || role === ROLE.INSTITUTE_ADMIN

  const teacherInstitutes = role === ROLE.TEACHER_ADMIN ? institutes.filter((data) => data?.coachingCentreName === userDetail?.coachingCentre.coachingCentreName) : institutes
  const teacherInstitutesLists = teacherInstitutes ? getInstituteDropdown(teacherInstitutes) : []
  const courseList = courses ? getCoursesDropdown(courses) : []
  const branchList = branches ? getBranchDropdown(branches) : []
  const batchList = batches ? getBatchDropdown(batches) : []
  const subjectList = subjects ? getCoursesDropdown(subjects) : []
  const chapterList = chapters ? getCoursesDropdown(chapters) : []
  const topicList = topics ? getCoursesDropdown(topics) : []
  const materialList = materials ? getMaterialsDropdown(materials) : []
  const teacherCourse = batchesData.map((data) => data?.course).flat()
  // eslint-disable-next-line no-unused-vars
  const teacherCourseDropDown = teacherCourse ? getCoursesDropdown(teacherCourse) : []
  const isDisabledDropdown = !!selectedPublishMaterial


  const validateField = (field: PublishMaterialField): void => {
    setErrors(
      validatePublishMaterialForm({
        values,
        errors,
        field
      })
    )
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    const validationError = validatePublishMaterialForm({
      values,
      errors
    })

    if (hasFormError(validationError)) {
      setErrors(validationError)
    } else {
      dispatch(resetMaterial())
      dispatch(selectedMaterial(null))
      dispatch(
        saveMaterialAccess({
          ...values,
          chapterId: [values.chapterId],
          coachingCenterId: values.coachingCentreId,
          batchIds: values.batchId,
          publishDate: format(startDate, DATE_FORMAT_YYYYMMDD),
          publishTime: format(time, TIME_FORMAT_HHMM)
        })
      )
    }
  }

  const resetValues = () => {
    dispatch(resetBatches())
    dispatch(resetBranches())
    dispatch(resetMaterial())
    dispatch(resetCourses())
    dispatch(resetChapters())
    dispatch(resetSubjects())
    dispatch(resetTopics())
  }

  const handleInstituteSelect = (institue: DropdownListProps) => {
    dispatch(getAllCoursesByInstitute(institue?.id))
    setValues({
      ...values,
      coachingCentreId: institue?.id
    })
    resetValues()
    setErrors(
      validatePublishMaterialForm({
        values: { ...values, coachingCentreId: institue?.id },
        errors,
        field: 'instituteName'
      })
    )
  }

  const handleCourseSelect = (course: DropdownListProps) => {
    dispatch(
      getBranchesForCourse({
        coachingCentreId: values.coachingCentreId || userDetail?.coachingCenterId,
        courseId: course?.id 
      })
    )
    dispatch(getChildCourses({ courseId: course?.id, type: 'SUBJECT' }))
    setValues({
      ...values,
      courseId: course?.id
    })
    setErrors(
      validatePublishMaterialForm({
        values: { ...values, courseId: course?.id },
        errors,
        field: 'coureName'
      })
    )
    dispatch(resetBatches())
    dispatch(resetBranches())
    dispatch(resetMaterial())
    dispatch(resetChapters())
    dispatch(resetSubjects())
    dispatch(resetTopics())
  }

  const handleBranchSelect = (branchs: any) => {
    const branch = branchs.map((value: DropdownListProps) => value?.id)
    dispatch(
      getBatchesForCourse({
        coachingCentreId: values.coachingCentreId|| userDetail?.coachingCenterId,
        courseId: values.courseId,
        branchId: branch  
      })
    )
    setValues({
      ...values,
      branchId: branch
    })
    setErrors(
      validatePublishMaterialForm({
        values: { ...values, branchId: branch },
        errors,
        field: 'branchName'
      })
    )
    dispatch(resetBatches())
    dispatch(resetMaterial())
  }

  const handleBatchSelect = (batchs: any) => {
    const batch = batchs.map((value: DropdownListProps) => value?.id)
    setValues({ ...values, batchId: batch })
    setErrors(
      validatePublishMaterialForm({
        values: { ...values, batchId: batch },
        errors,
        field: 'batchName'
      })
    )
    dispatch(resetMaterial())
  }

  const handleSubjectSelect = (subject: DropdownListProps) => {
    setValues({ ...values, subjectId: subject?.id })
    dispatch(getChildCourses({ courseId: subject?.id, type: 'CHAPTER' }))
    setErrors(
      validatePublishMaterialForm({
        values: { ...values, subjectId: subject?.id },
        errors,
        field: 'subjectName'
      })
    )
    dispatch(resetMaterial())
    dispatch(resetChapters())
    dispatch(resetTopics())
  }

  const handleChapterSelect = (chapter: DropdownListProps) => {
    setValues({ ...values, chapterId: chapter?.id })
    dispatch(getChildCourses({ courseId: chapter?.id, type: 'TOPIC' }))
    dispatch(resetMaterial())
    dispatch(resetTopics())
  }

  const handleTopicSelect = (topic: DropdownListProps) => {
    setValues({ ...values, topicId: topic?.id ? topic?.id : null })
    dispatch(
      getMaterialsWithPublishStatus({
        topicId: topic?.id,
        batchId: values.batchId
      })
    )
    dispatch(resetMaterial())
  }

  const handleMaterialSelect = (material: DropdownListProps) => {
    setValues({ ...values, materialId: material?.id ? material?.id : null })
  }

  useEffect(() => {
    if (shouldHideInstitutes) {
      const instituteId = userDetail?.coachingCenterId || ''
      dispatch(getAllCoursesByInstitute(instituteId))
      setValues({
        ...values,
        coachingCentreId: instituteId
      })
    } else {
      if (!isDisabledDropdown) {
        dispatch(getInstitutes())
        resetValues()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isEdit) {
      dispatch(selectedMaterial(null))
      resetValues()
      setValues({
        ...values,
        coachingCentreId: ''
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit])

  return (
    <PageWrapper id="container">
      <SectionTitle title={chapterPublish} hasBorder />
      {isLoading ? (
        <Loader />
      ) : (
        <FlexWrapper>
          {!shouldHideInstitutes && (
            <DropdownWrapper>
              <EditableDropdown
                dropdownList={teacherInstitutesLists}
                title={instituteName}
                placeholder={instituteName}
                isRequired
                isDisabled={isDisabledDropdown}
                error={errors.instituteName}
                onBlur={() => validateField('instituteName')}
                handleSelect={(institue) => handleInstituteSelect(institue)}
                defaultValue={{
                  id: values?.coachingCentreId || '',
                  name: values?.coachingCentreId
                    ? teacherInstitutesLists.filter(
                      (institue) => institue?.id === values?.coachingCentreId
                    )[0]?.name
                    : ''
                }}
              />
            </DropdownWrapper>
          )}
          <DropdownWrapper>
            <EditableDropdown
              dropdownList={courseList}
              title={selectCourse}
              placeholder={selectCourse}
              isRequired
              isDisabled={isDisabledDropdown}
              onBlur={() => validateField('coureName')}
              error={errors.coureName}
              handleSelect={(course) => handleCourseSelect(course)}
              defaultValue={{
                id: values?.courseId || '',
                name: values?.courseId
                  ? courseList.filter(
                    (course) => course?.id === values?.courseId
                  )[0]?.name
                  : ''
              }} />
          </DropdownWrapper>
          <DropdownWrapper>
            <EditableDropdown
              dropdownList={branchList}
              title={selectBranch}
              placeholder={selectBranch}
              isRequired
              isMultiChoice
              isDisabled={isDisabledDropdown}
              onBlur={() => validateField('branchName')}
              error={errors.branchName}
              handleMultiSelect={(branchs) => handleBranchSelect(branchs)}
              defaultValue={{
                id: values?.branchId || '',
                name: values?.branchId
                  ? branchList?.filter(
                    (branch) => branch?.id === values?.branchId
                  )[0]?.name
                  : ''
              }} />
          </DropdownWrapper>
          <DropdownWrapper>
            <EditableDropdown
              dropdownList={batchList}
              title={selectBatch}
              placeholder={selectBatch}
              isRequired
              isMultiChoice
              isDisabled={isDisabledDropdown}
              onBlur={() => validateField('batchName')}
              error={errors.batchName}
              handleMultiSelect={(batchs) => handleBatchSelect(batchs)}
              defaultValue={{
                id: values?.batchId || '',
                name: values?.batchId
                  ? batchList?.filter((batch) => batch?.id === values?.batchId)[0]
                    ?.name
                  : ''
              }} />
          </DropdownWrapper>
          <DropdownWrapper>
            <EditableDropdown
              dropdownList={subjectList}
              title={`${selectSubject}`}
              placeholder={selectSubject}
              isRequired
              isDisabled={isDisabledDropdown}
              onBlur={() => validateField('subjectName')}
              error={errors.subjectName}
              handleSelect={(subject) => handleSubjectSelect(subject)}
              defaultValue={{
                id: values?.subjectId || '',
                name: values?.subjectId
                  ? subjectList.filter(
                    (subject) => subject?.id === values?.subjectId
                  )[0]?.name
                  : ''
              }}
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <EditableDropdown
              dropdownList={chapterList}
              title={`${selectChapter}`}
              placeholder={selectChapter}
              isDisabled={isDisabledDropdown}
              handleSelect={(chapter) => handleChapterSelect(chapter)}
              defaultValue={{
                id: values?.chapterId || '',
                name: values?.chapterId
                  ? chapterList.filter(
                    (chapter) => chapter?.id === values?.chapterId
                  )[0]?.name
                  : ''
              }}
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <EditableDropdown
              dropdownList={topicList}
              title={`${selectTopic}`}
              placeholder={selectTopic}
              isDisabled={isDisabledDropdown}
              handleSelect={(topic) => handleTopicSelect(topic)}
              defaultValue={{
                id: values?.topicId || '',
                name: values?.topicId
                  ? topicList.filter((topic) => topic?.id === values?.topicId)[0]
                    ?.name
                  : ''
              }}
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <EditableDropdown
              dropdownList={materialList}
              title={`${selectMaterial}`}
              placeholder={selectMaterial}
              isDisabled={isDisabledDropdown}
              defaultValue={{
                id: values?.materialId || '',
                name: values?.materialId
                  ? materialList.filter(
                    (material) => material?.id === values?.materialId
                  )[0]?.name
                  : ''
              }}
              handleSelect={(material) => handleMaterialSelect(material)}
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <DatePicker
              selected={startDate}
              onSelect={(date) => setStartDate(date)}
              onChange={(date) => setStartDate(date)}
              placeholderText={publishDate}
              minDate={new Date()}
              customInput={
                <Input
                  value={startDate}
                  inputType="text"
                  label={publishDate}
                  placeholder={publishDate}
                  onChange={(date) => setStartDate(date)}
                  suffix={['far', 'calendar']}
                />
              }
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <DatePicker
              selected={time}
              onSelect={(time) => setTime(time)}
              onChange={(time) => setTime(time)}
              showTimeSelectOnly
              showTimeSelect
              timeIntervals={30}
              timeCaption="Time"
              dateFormat="h:mm aa"
              popperPlacement="bottom"
              placeholderText={publishTiming}
              customInput={
                <Input
                  value={time}
                  inputType="text"
                  label={publishTiming}
                  placeholder={publishTiming}
                  onChange={(date) => setStartDate(date)}
                  suffix={['far', 'clock']}
                />
              }
            />
          </DropdownWrapper>
        </FlexWrapper>
      )}
      <FlexWrapper justifyContent="center">
        <Button onClick={handleSubmit}>{publish}</Button>
      </FlexWrapper>
    </PageWrapper>
  )
}

export default PublishMaterial
